import { SimpleSelect } from "@/src/common/components/Select"
import { FetcherError } from "@/src/common/data-layer/datasources/ExternalHTTP"
import { City } from "@/src/common/types"
import { SB_CITIES_API_URL } from "@/src/sb/business-layer/config"
import { SBAPIInstance } from "@/src/sb/data-layer"
import { debounce } from "lodash"
import { useSession } from "next-auth/react"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  InputActionMeta,
  MenuProps,
  OptionProps,
  PropsValue,
  SingleValueProps,
  components
} from "react-select"

export type CityOption = {
  value: string
  label: string
} & City

type CityGroup = { label: string; options: Array<CityOption> }

const CitySingleValue = ({
  ...props
}: SingleValueProps<CityOption, false, CityGroup>) => {
  const selectedOptionValue =
    (props.selectProps?.value as Pick<CityOption, "value">).value ?? ""

  // Check for the position of extra city information like state presumably seperated by ","
  const extraCityComponentPosition = selectedOptionValue.indexOf(",")

  return (
    <components.SingleValue {...props}>
      <section>
        <div className={"!max-w-full"}>
          {extraCityComponentPosition > -1 ? (
            <>
              <span className={"font-medium"}>
                {selectedOptionValue.substring(0, extraCityComponentPosition)}
              </span>
              <span className={"font-light"}>
                {selectedOptionValue.substring(extraCityComponentPosition)}
              </span>
            </>
          ) : (
            <span>{selectedOptionValue}</span>
          )}
        </div>
      </section>
    </components.SingleValue>
  )
}

const CityMenuOption = (props: OptionProps<CityOption, false, CityGroup>) => {
  const data = props.data as Record<string, any>
  return (
    <components.Option {...props}>
      <section>
        <div className={"!max-w-full"}>
          <span className={"font-medium"}>{data.name}</span>,{" "}
          <span className={"font-light"}>{data.state}</span>
        </div>
        <div className="text-gray-400 text-sm">{data.country}</div>
      </section>
    </components.Option>
  )
}

const CityMenu = (props: MenuProps<CityOption, false, CityGroup>) => {
  return props.selectProps.inputValue ? (
    <components.Menu<CityOption, false, CityGroup> {...props}>
      {props.children}
    </components.Menu>
  ) : null
}

type CitySelectProps = {
  value?: string | null
  onChange: (selected: { city: string; country: string }) => void
  onBlur?: (e: React.FocusEvent) => void
  errors?: Array<string>
  name?: string
  inputId?: string
  placeholder?: string
  classNames?: Record<string, () => string>
}

const CitySelect = ({
  value,
  onChange,
  onBlur,
  errors,
  name = "city",
  inputId,
  placeholder = "Enter city name (e.g., Lagos)",
  classNames
}: CitySelectProps) => {
  const { data: session } = useSession()
  const token = (session?.user as any)?.accessToken
  const [cityOptions, setCityOptions] = useState<CityOption[]>([])
  const [loadingCityOptions, setLoadingCityOptions] = useState(false)

  const latestSearch = useRef(0)

  const fetchCities = useMemo(
    () =>
      debounce((searchString: string, actionMeta: InputActionMeta) => {
        if (searchString.length < 3 || actionMeta.action !== "input-change") {
          return
        }
        if (!token) {
          console.warn("Skipping city lookup: the member has no access token")
          return
        }

        const search = ++latestSearch.current
        setLoadingCityOptions(true)

        SBAPIInstance.fetchCities(SB_CITIES_API_URL, token, {
          q: searchString.toLowerCase().trim()
        })
          .then((data) => {
            if (search !== latestSearch.current) return
            setCityOptions(
              data.map((city) => ({
                ...city,
                value: `${city.name}, ${city.state}`,
                label: `${city.name}, ${city.state}, ${city.country}`
              }))
            )
          })
          .catch((error: FetcherError) => {
            if (search !== latestSearch.current) return
            setCityOptions([])
            console.warn("City lookup failed", {
              url: SB_CITIES_API_URL,
              status: error?.status,
              info: error?.info,
              data: error?.data
            })
          })
          .finally(() => {
            if (search === latestSearch.current) setLoadingCityOptions(false)
          })
      }, 500),
    [token]
  )

  useEffect(() => () => fetchCities.cancel(), [fetchCities])

  return (
    <SimpleSelect
      placeholder={placeholder}
      components={{
        DropdownIndicator: null,
        Menu: CityMenu,
        Option: CityMenuOption,
        SingleValue: CitySingleValue
      }}
      onInputChange={fetchCities}
      filterOption={null}
      onMenuClose={() => setCityOptions([])}
      isLoading={loadingCityOptions}
      value={
        (value
          ? { value, label: value }
          : "") as unknown as PropsValue<CityOption>
      }
      getOptionValue={(option: { name: any; state: any }) =>
        `${option.name}, ${option.state}`
      }
      onChange={(selectedOption: { value: any; country: any }) =>
        onChange({
          city: selectedOption?.value ?? "",
          country: selectedOption?.country ?? ""
        })
      }
      options={cityOptions}
      name={name}
      inputId={inputId}
      onBlur={onBlur}
      errors={errors}
      {...(classNames ? { classNames } : {})}
    />
  )
}

export default CitySelect
